import { c as _c } from "react-compiler-runtime";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Raven from 'raven-js';
import { Metrics } from 'collaboration-sidebar/utils/metrics';
import { HAS_UNRECOVERABLE_ERROR } from '../../constants/CollaborationSidebarStateProperties';
import debounce from '../../utils/debounce';
import ChildrenErrorBoundary from './ChildrenErrorBoundary';
import { jsx as _jsx } from "react/jsx-runtime";
const TIME_TO_RESET = 3000;
function useRenderCount() {
  'use no memo';

  const renderCount = useRef(0);
  const debouncedReset = useMemo(() => debounce(() => {
    renderCount.current = 0;
  }, TIME_TO_RESET), []);
  useEffect(() => {
    renderCount.current += 1;
    debouncedReset();
  });

  // React compiler does not like accessing the ref during render
  return renderCount.current;
}

/**
 * From: https://git.hubteam.com/HubSpot/Critsit/issues/4039
 * This is a somehow hacky approach but could be useful for us to remediate errors when ErrorBoundary is not enough.
 *
 * This component will render its children until a certain threshold and then it'll stop rendering and notify us in Grafana.
 *
 * It also uses ChildrenErrorBoundary to fall back to rendering the app's original children when an error is thrown
 * from inside collaboration sidebar's components.
 */
export default function ErrorCountWrapper(t0) {
  "use memo";

  const $ = _c(17);
  const {
    appName,
    children,
    errorBoundaryFingerprint,
    threshold,
    onUpdateCollaborationSidebarState,
    renderFallback
  } = t0;
  const renderCount = useRenderCount();
  const hasRenderedTooMuch = renderCount > threshold;
  let t1;
  if ($[0] !== onUpdateCollaborationSidebarState) {
    t1 = () => {
      onUpdateCollaborationSidebarState({
        [HAS_UNRECOVERABLE_ERROR]: true
      });
    };
    $[0] = onUpdateCollaborationSidebarState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const setGlobalError = t1;
  let t2;
  let t3;
  if ($[2] !== appName || $[3] !== errorBoundaryFingerprint || $[4] !== hasRenderedTooMuch || $[5] !== setGlobalError) {
    t2 = () => {
      if (!hasRenderedTooMuch) {
        return;
      }
      setGlobalError();
      console.error(`[${appName}] ${errorBoundaryFingerprint} rendered too many times`);
      Metrics.counter("infinite-loop", {
        app_name: appName
      }).increment();
      Raven.captureException(new Error(`[${appName}] ${errorBoundaryFingerprint} rendered too many times`), {
        extra: {
          appName
        },
        fingerprint: [errorBoundaryFingerprint],
        tags: {
          lib: "collaboration-sidebar"
        }
      });
    };
    t3 = [appName, errorBoundaryFingerprint, hasRenderedTooMuch, setGlobalError];
    $[2] = appName;
    $[3] = errorBoundaryFingerprint;
    $[4] = hasRenderedTooMuch;
    $[5] = setGlobalError;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  if (hasRenderedTooMuch) {
    let t4;
    if ($[8] !== renderFallback) {
      t4 = renderFallback();
      $[8] = renderFallback;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  let t4;
  if ($[10] !== renderFallback) {
    t4 = renderFallback();
    $[10] = renderFallback;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== children || $[13] !== errorBoundaryFingerprint || $[14] !== setGlobalError || $[15] !== t4) {
    t5 = /*#__PURE__*/_jsx(ChildrenErrorBoundary, {
      fingerprint: errorBoundaryFingerprint,
      onError: setGlobalError,
      originalChildren: t4,
      children: children
    });
    $[12] = children;
    $[13] = errorBoundaryFingerprint;
    $[14] = setGlobalError;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}